import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ChromeComponentActions,
  PlayerApiActions,
  PlayerComponentActions,
  PlayerDetailsPageActions,
} from './players.actions';
import {
  catchError,
  delay,
  EMPTY,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { PlayerApiClient } from '@pool-league/frontend-shared-data-api';
import { isDefined } from '@pool-league/shared-util-misc';
import { toError } from '@pool-league/error';
import { UserInfoService } from '@pool-league/frontend-shared-util-auth';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { playersFeature } from './players.feature';
import { Player } from './players.state';
import { SetOptional } from '@pool-league/shared-util-misc';

@Injectable()
export class PlayersEffects {
  readonly #actions = inject(Actions);
  readonly #me = inject(UserInfoService).getMe();
  readonly #playerApiClient = inject(PlayerApiClient);
  readonly #store = inject(Store);

  readonly preloadPlayers = createEffect(() =>
    this.#actions.pipe(
      ofType(ChromeComponentActions.loadPlayers),
      exhaustMap(() => {
        return this.#playerApiClient.getAllPlayers().pipe(
          map((players) =>
            players.map((player) => ({
              ...player,
              isMe: player.bbUserName === this.#me.bbUserName,
            })),
          ),
          map((players) => PlayerApiActions.playersLoaded({ players })),
          catchError((error: unknown) =>
            of(
              PlayerApiActions.initialPlayerLoadFailed({
                error: toError(error),
              }),
            ),
          ),
        );
      }),
    ),
  );

  /**
   * Load a player's profile for display in the player details page
   */
  readonly loadPlayerProfile$ = createEffect(() =>
    this.#actions.pipe(
      ofType(PlayerDetailsPageActions.openPlayerProfile),
      map(({ bbUserName }) => bbUserName),
      concatLatestFrom((bbUserName) =>
        this.#store.select(playersFeature.selectPlayerByBbUserName(bbUserName)),
      ),
      map(([bbUserName, playerData]) =>
        this.#buildFallbackProfile(bbUserName, playerData),
      ),
      switchMap((fallback) => this.#loadFullPlayerProfile(fallback)),
    ),
  );

  #buildFallbackProfile(bbUserName: string, player: Player | undefined) {
    const isMe = bbUserName === this.#me.bbUserName;
    const avatar = isMe ? this.#me.idpAvatar : player?.avatar;
    return {
      bbUserName,
      displayName: bbUserName,
      retired: false,
      isMe,
      ...player,
      avatar,
    };
  }

  /**
   * If the given fallback has an id, load the full player profile.  If not,
   * check if a player exists for the bbUserName in the fallback value and if so
   * load the full profile for that player (and update the cached player list),
   * otherwise emit a player not found event.
   *
   * @param fallback
   * @private
   */
  #loadFullPlayerProfile(fallback: SetOptional<Player, 'id'>) {
    const obs = hasId(fallback)
      ? this.#loadFullPlayerProfileById(fallback)
      : this.#playerApiClient.getByBbUserName(fallback.bbUserName).pipe(
          switchMap((response) => {
            if (!hasId(response)) {
              return of(PlayerApiActions.playerNotFound({ fallback }));
            }
            const mergedFallback = {
              ...fallback,
              ...response,
            };
            return this.#loadFullPlayerProfileById(mergedFallback).pipe(
              startWith(
                PlayerApiActions.playerLoaded({
                  player: mergedFallback,
                }),
              ),
            );
          }),
        );
    return obs.pipe(
      startWith(PlayerApiActions.profileLoading({ profile: fallback })),
      catchError((error) =>
        of(PlayerApiActions.profileLoadError({ fallback, error })),
      ),
    );
  }

  #loadFullPlayerProfileById(fallback: Player) {
    return this.#playerApiClient.getFullProfile(fallback.id).pipe(
      map((profile) =>
        PlayerApiActions.profileLoadSuccess({
          profile: {
            ...fallback,
            ...profile,
          },
        }),
      ),
      catchError((error) =>
        of(
          PlayerApiActions.profileLoadError({
            fallback,
            error,
          }),
        ),
      ),
    );
  }

  /**
   * Attempt to load player data for a player whose ID has been passed to the
   * player component, but does not exist in the player cache.
   */
  readonly loadPlayerById$ = createEffect(() =>
    this.#actions.pipe(
      ofType(PlayerComponentActions.loadPlayer),
      mergeMap(({ id }) => this.#maybeLoadPlayer(id)),
    ),
  );

  readonly #loadPlayerRequestInFlight: Record<string, boolean> = {};
  readonly #loadPlayerBackoff: Record<string, number> = {};

  #maybeLoadPlayer(id: string) {
    if (this.#loadPlayerRequestInFlight[id]) {
      return EMPTY;
    }
    console.warn(`Player data for ${id} not preloaded - loading now.`);
    this.#loadPlayerRequestInFlight[id] = true;
    return this.#playerApiClient.getByIdIfExists(id).pipe(
      catchError((loadError) => {
        // Prevent retrying to load this player for a while by not clearing the
        // loadPlayerRequestInFlight flag until after an exponential backoff delay
        this.#loadPlayerBackoff[id] ||= 30;
        const backOff = (this.#loadPlayerBackoff[id] *= 2);
        console.error(
          `Error loading player ${id}. Will not retry for at least ${backOff} seconds.`,
          loadError,
        );
        return of(undefined).pipe(delay(backOff * 1000));
      }),
      tap(() => (this.#loadPlayerRequestInFlight[id] = false)),
      filter(isDefined),
      map((player) => ({
        ...player,
        isMe: this.#me.bbUserName === player.bbUserName,
      })),
      map((player) => PlayerApiActions.playerLoaded({ player })),
    );
  }
}

function hasId(value?: { id?: string }): value is { id: string } {
  return !!value?.id;
}
